import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://dailybrickdeals.com";
const SITE_NAME = "BrickDailyDeals";
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;
const DEFAULT_DESCRIPTION =
  "Le migliori offerte quotidiane sui set di costruzioni. Amazon, eBay, AliExpress: sconti curati per appassionati e collezionisti.";

/**
 * Per-page SEO meta tags (title, description, canonical, Open Graph, Twitter Card).
 * Pass `path` to derive the canonical URL automatically.
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
}) {
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} — Offerte set di costruzioni`;
  const url = `${SITE_URL}${path.startsWith("/") ? path : "/" + path}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:image" content={image} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}

/**
 * Inject structured data (JSON-LD) into the document head.
 */
export function JsonLd({ data }) {
  if (!data) return null;
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

export const STORE_FOR_BRAND = {
  amazon: "Amazon",
  ebay: "eBay",
  aliexpress: "AliExpress",
  zavvi: "Zavvi",
  lego: "LEGO Shop",
  other: "Altro",
};

/**
 * Pre-built schemas for common entities.
 */
export const schemas = {
  organization: () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    description: DEFAULT_DESCRIPTION,
    sameAs: ["https://t.me/BrickDailyDeals"],
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "info@dailybrickdeals.com",
        availableLanguage: ["Italian", "English"],
      },
    ],
  }),

  website: () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "it-IT",
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  }),

  breadcrumb: (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
    })),
  }),

  /**
   * Product + Offer schema for deal pages. Helps Google show price/discount in SERPs.
   */
  product: (deal) => {
    if (!deal) return null;
    const brand = STORE_FOR_BRAND[deal.store] || deal.store;
    const name = (deal.title_display && deal.title_display.trim()) || deal.title;
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name,
      image: deal.image_url || DEFAULT_IMAGE,
      description:
        deal.analisi_breve?.slice(0, 280) ||
        deal.description?.slice(0, 280) ||
        `Offerta su ${name} con sconto del ${
          deal.discount_percent || 0
        }%. Acquista su ${brand}.`,
      brand: { "@type": "Brand", name: brand },
      offers: {
        "@type": "Offer",
        url: `${SITE_URL}/offerta/${deal.id}`,
        priceCurrency: "EUR",
        price: deal.price_current ?? 0,
        ...(deal.price_original
          ? { highPrice: deal.price_original }
          : {}),
        availability: "https://schema.org/InStock",
        priceValidUntil: deal.expires_at
          ? new Date(deal.expires_at).toISOString().split("T")[0]
          : new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0],
        seller: { "@type": "Organization", name: brand },
      },
    };
  },

  /**
   * FAQPage schema — turns the FAQ block on a deal page into rich snippets on Google.
   * Pass the deal.faq array directly.
   */
  faqPage: (faq) => {
    if (!Array.isArray(faq) || faq.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    };
  },

  /**
   * ItemList of latest deals on the homepage.
   */
  itemList: (deals) => {
    if (!deals || !deals.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Ultime offerte BrickDailyDeals",
      numberOfItems: deals.length,
      itemListElement: deals.slice(0, 20).map((d, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}/offerta/${d.id}`,
        name: (d.title_display && d.title_display.trim()) || d.title,
      })),
    };
  },
};
